import "./Tukey.css";

import { Alert, Badge, Input, Table } from "reactstrap";
import { useMemo, useState } from "react";

import { ComponentPlaceholder } from "../../../../../components/componentPlaceholder/componentPlaceholder";
import { ComponentTitle } from "../../../../../components/componentTitle/componentTitle";
import PropTypes from "prop-types";
import { PropertyBadge } from "../../../../../components/Badges/PropertyBadge/PropertyBadge";
import { PropertyRow } from "../PropertyRow";
import { TableVirtuoso } from "react-virtuoso";
import { truncateNumber } from "../../../../../services/utils";

export const Tukey = ({
  computedResult,
  displayedTukey,
  closeTukey,
  tukeyIsLoading,
}) => {
  const results = computedResult?.tukey?.[displayedTukey] ?? [];
  const errors = computedResult?.tukeyErrors?.[displayedTukey] ?? "";

  return (
    computedResult &&
    displayedTukey && (
      <TukeyTable
        factor={displayedTukey}
        computedTukey={results}
        computedTukeyError={errors}
        closeTukey={closeTukey}
        tukeyIsLoading={tukeyIsLoading}
      />
    )
  );
};

const TukeyTable = ({
  factor,
  computedTukey,
  computedTukeyError,
  closeTukey,
  tukeyIsLoading,
}) => {
  const [search, setSearch] = useState("");

  const tukeyRows = useMemo(
    () =>
      computedTukey?.filter((value) =>
        `${value.group1} ${value.group2}`
          .toLowerCase()
          .includes(search.toLowerCase())
      ),
    [computedTukey, search]
  );

  return (
    <div className="powerdash-component gap-1">
      <ComponentTitle
        icon="fa-solid fa-code-compare"
        title={
          <label className="d-flex align-items-center gap-2">
            Tukey HSD :
            <Badge className="hiphen-badge small">
              <PropertyRow name={factor} />
            </Badge>
            <Input
              bsSize="sm"
              type="search"
              value={search}
              placeholder="Filter groups"
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
        }
        topRightContent={
          <i
            className="fa-solid fa-lg fa-xmark clickable me-1 discrete-icon"
            onClick={closeTukey}
          />
        }
      />
      {factor ? (
        <>
          {computedTukey.length === 0 && tukeyIsLoading ? (
            <ComponentPlaceholder
              icon="fa-solid fa-spinner fa-spin"
              text="Calculating tukey HSD"
            />
          ) : (
            <div className="tukey-table">
              {computedTukey.length === 0 ? (
                <Alert color="danger" className="d-flex flex-column gap-1">
                  <span>
                    Something went wrong during tukey HSD calculation on{" "}
                    {factor}. Please verify that your data is suitable for this
                    analysis.
                  </span>
                  {computedTukeyError && (
                    <code className="p-2">{computedTukeyError}</code>
                  )}
                </Alert>
              ) : (
                <TableVirtuoso
                  data={tukeyRows}
                  totalCount={tukeyRows.length}
                  components={{ Table: (props) => <Table {...props}></Table> }}
                  fixedHeaderContent={() => (
                    <tr>
                      <th>Group</th>
                      <th>meandiff</th>
                      <th>lower (95%)</th>
                      <th>upper (95%)</th>
                      <th>p-adj</th>
                      <th></th>
                    </tr>
                  )}
                  computeItemKey={(_, value) =>
                    `${value.group1}-${value.group2}`
                  }
                  itemContent={(_, value) => (
                    <>
                      <td>
                        <span className="property d-flex align-items-center gap-2">
                          <PropertyBadge
                            hideName
                            technicalName={factor}
                            value={value.group1}
                          />
                          <i className="fa-solid fa-minus discrete-icon" />
                          <PropertyBadge
                            hideName
                            technicalName={factor}
                            value={value.group2}
                          />
                        </span>
                      </td>
                      <td>{truncateNumber(value.meandiff)}</td>
                      <td>{truncateNumber(value.lower)}</td>
                      <td>{truncateNumber(value.upper)}</td>
                      <td>
                        {Number.parseFloat(value["p-adj"]).toExponential(2)}
                      </td>
                      <td className="text-center">
                        {value["p-adj"] < 0.05 && (
                          <i className="fa-solid fa-star" />
                        )}
                        {value["p-adj"] < 0.01 && (
                          <i className="fa-solid fa-star" />
                        )}
                        {value["p-adj"] < 0.001 && (
                          <i className="fa-solid fa-star" />
                        )}
                      </td>
                    </>
                  )}
                />
              )}
            </div>
          )}
        </>
      ) : (
        <ComponentPlaceholder icon="fa-solid fa-table" />
      )}
    </div>
  );
};

Tukey.propTypes = {
  computedResult: PropTypes.shape({
    anova: PropTypes.object,
    linear_model: PropTypes.string,
    anova_error: PropTypes.string,
    experiment: PropTypes.string,
    date: PropTypes.string,
    length: PropTypes.number,
    datetime: PropTypes.string,
    tukey: PropTypes.object,
    tukeyErrors: PropTypes.object,
  }),
  displayedTukey: PropTypes.string,
  tukeyIsLoading: PropTypes.bool,
  closeTukey: PropTypes.func.isRequired,
};

TukeyTable.propTypes = {
  factor: PropTypes.string.isRequired,
  // Always an array: the parent passes `results ?? []`. That default is exactly
  // why `!computedTukey` never fired and both the spinner and the error alert
  // below it were dead code (R1-22): an empty array is truthy.
  computedTukey: PropTypes.arrayOf(
    PropTypes.shape({
      group1: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      group2: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      meandiff: PropTypes.number,
      lower: PropTypes.number,
      upper: PropTypes.number,
      "p-adj": PropTypes.number,
    })
  ),
  computedTukeyError: PropTypes.string,
  tukeyIsLoading: PropTypes.bool,
  closeTukey: PropTypes.func.isRequired,
};
